/**
 * Pure payloads for classic behaviour the v2 screens call.
 * No DOM, no logging of secrets.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SECRET_KEY = /password|token|secret|api_key|credential/i;

export const ROSTER_EMPTY = 'Everyone with access is already in this chat.';

/** @param {string} productId */
export function rosterPath(productId) {
  return `/products/${encodeURIComponent(productId)}/roster`;
}

/**
 * People on the product roster who are not already in the chat.
 * @param {Array<{ id?: string }>|null|undefined} users
 * @param {Array<{ id?: string }>|null|undefined} members
 */
export function rosterCandidates(users, members) {
  const taken = new Set((members || []).map((member) => member && member.id).filter(Boolean));
  return (users || []).filter((user) => user && user.id && !taken.has(user.id));
}

/**
 * @param {string} password
 * @returns {{ ok: true, body: { password: string } } | { ok: false, error: string }}
 */
export function passwordResetBody(password) {
  const value = String(password || '');
  if (value.length < 6) return { ok: false, error: 'Password must be at least 6 characters' };
  return { ok: true, body: { password: value } };
}

/**
 * @param {{ mentions?: boolean, reports?: boolean }} patch
 */
export function notifyPrefBody(patch) {
  const body = {};
  if (patch && patch.mentions != null) body.notify_email_mentions = !!patch.mentions;
  if (patch && patch.reports != null) body.notify_email_reports = !!patch.reports;
  return body;
}

/** @param {boolean} enabled */
export function digestPrefBody(enabled) {
  return { enabled: !!enabled };
}

/** @param {{ insights?: Array<{ id?: string }>, error?: string }|null|undefined} insights */
export function seenBody(insights) {
  if (!insights || insights.error) return null;
  const ids = (insights.insights || [])
    .map((card) => card && card.id)
    .filter((id) => id && UUID_RE.test(String(id)));
  if (!ids.length) return null;
  return { insight_ids: ids };
}

/**
 * @param {string} content
 * @returns {{ ok: true, body: { content: string } } | { ok: false, error: string }}
 */
export function correctionBody(content) {
  const text = String(content || '').trim().slice(0, 2000);
  if (!text) return { ok: false, error: 'Write what Bonnie should remember' };
  return { ok: true, body: { content: text } };
}

/** @param {string} productId */
export function reindexBody(productId) {
  return { product_id: productId };
}

/**
 * At most once per session per product, and only for an admin who has documents.
 * @param {{ claimed?: { has: (id: string) => boolean }, productId?: string, isAdmin?: boolean, docCount?: number }} spec
 */
export function shouldReindex(spec) {
  const claimed = spec && spec.claimed;
  const productId = spec && spec.productId;
  if (!spec || !spec.isAdmin || !productId || !(Number(spec.docCount) > 0)) return false;
  if (claimed && claimed.has(productId)) return false;
  return true;
}

/** @param {string} id */
export function syncDocsPath(id) {
  return `/connectors/${encodeURIComponent(id)}/sync-docs`;
}

/** @param {string} id */
export function syncSourcePath(id) {
  return `/cdl/sources/${encodeURIComponent(id)}/sync`;
}

/**
 * Classify POST /cdl/sources/:id/sync for a toast.
 * @param {object|null|undefined} result
 * @returns {{ tone: 'info'|'error'|'success', message: string }}
 */
export function syncOutcome(result) {
  if (!result) return { tone: 'error', message: 'Sync failed' };
  const text = String(result.message || result.error || '');
  if (result.cooldown || /15 minute/i.test(text)) {
    return { tone: 'info', message: result.message || result.error || 'Sync allowed once per 15 minutes' };
  }
  if (result.running) {
    return { tone: 'info', message: result.message || 'Sync already in progress' };
  }
  if (result.ok === false || result.error) {
    return { tone: 'error', message: result.message || result.error || 'Sync failed' };
  }
  return { tone: 'success', message: 'Sync finished' };
}

/**
 * Primary synced source for the dashboard: store payload, else the first synced connector.
 * @param {{ insights?: { cdl?: { source_id?: string } }, connectors?: { integrations?: object[] } }|null|undefined} data
 */
export function dashboardSourceId(data) {
  const fromStore = data && data.insights && data.insights.cdl && data.insights.cdl.source_id;
  if (fromStore) return fromStore;
  const rows = (data && data.connectors && data.connectors.integrations) || [];
  const hit = rows.find((row) => row && row.mode === 'synced' && row.source_id);
  return hit ? hit.source_id : null;
}

/** @param {{ page?: number, limit?: number, action?: string }} spec */
export function auditListPath(spec) {
  const params = new URLSearchParams();
  params.set('page', String((spec && spec.page) || 1));
  params.set('limit', String((spec && spec.limit) || 20));
  if (spec && spec.action) params.set('action', spec.action);
  return `/audit-logs?${params}`;
}

/** @param {object|null|undefined} log */
export function auditDetail(log) {
  const raw = log && log.new_values;
  let values = raw;
  if (typeof raw === 'string') {
    try { values = JSON.parse(raw); } catch { return ''; }
  }
  if (!values || typeof values !== 'object') return '';
  return Object.entries(values)
    .filter(([key]) => !SECRET_KEY.test(key))
    .slice(0, 2)
    .map(([key, value]) => `${key}: ${plainValue(value)}`)
    .join(', ');
}

/** @param {number|string} days */
export function activityPath(days) {
  const n = Number(days) === 90 ? 90 : 30;
  return `/kpi/users?days=${n}`;
}

/** @param {object|null|undefined} data */
export function activityRows(data) {
  const rows = [];
  for (const row of (data && data.signup_trend) || []) {
    rows.push({ date: cellDate(row.date), metric: 'signups', user: '', count: row.count });
  }
  for (const row of (data && data.active_per_day) || []) {
    rows.push({ date: cellDate(row.date), metric: 'active_users', user: '', count: row.count });
  }
  for (const row of (data && data.top_users) || []) {
    rows.push({
      date: '',
      metric: 'top_user_action',
      user: row.name || row.email || '',
      count: row.action_count,
    });
  }
  return rows;
}

/** @param {Array<Record<string, unknown>>} rows @param {Array<{ label: string, value: (row: object) => unknown }>} columns */
export function toCsv(rows, columns) {
  const lines = [columns.map((column) => csvCell(column.label)).join(',')];
  for (const row of rows || []) {
    lines.push(columns.map((column) => csvCell(column.value(row))).join(','));
  }
  return lines.join('\n');
}

/** @param {object[]} logs */
export function auditCsv(logs) {
  return toCsv(logs, [
    { label: 'action', value: (row) => row.action || '' },
    { label: 'entity', value: (row) => row.entity_type || '' },
    { label: 'user', value: (row) => row.user_name || row.user_email || '' },
    { label: 'details', value: (row) => auditDetail(row) },
    { label: 'date', value: (row) => cellDate(row.created_at) },
  ]);
}

/** @param {object|null|undefined} data */
export function activityCsv(data) {
  return toCsv(activityRows(data), [
    { label: 'date', value: (row) => row.date },
    { label: 'metric', value: (row) => row.metric },
    { label: 'user', value: (row) => row.user },
    { label: 'count', value: (row) => row.count },
  ]);
}

function plainValue(value) {
  if (value == null || typeof value === 'object') return '';
  return String(value);
}

function cellDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toISOString();
}

function csvCell(value) {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}
